/** @jsx jsx */
/** @jsxFrag */
import React, { useCallback, useState, type FC, useContext } from 'react';
import { useIntl } from 'react-intl';
// eslint-disable-next-line no-unused-vars -- jsx is used implicitly by the @jsx pragma
import { css, jsx } from '@compiled/react';
import type { MessageDescriptor } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';

import { Box, xcss } from '@atlaskit/primitives';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import { AIButtonUI } from '@atlassian/ai-smart-button';

import { fg } from '@confluence/feature-gating';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { CommentCountsProvider } from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import { CustomPopupContainer } from '@confluence/quick-summary/entry-points/CustomPopupContainer.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';

import { ContextualButtonPulse } from './platform-ui/ContextualButtonPulse.tsx';
import { useDynamicAIContext } from './hooks/useDynamicAIContext.tsx';
import { useAIFloatingButtonNudge } from './hooks/useAIFloatingButtonNudge.tsx';
import { AIFloatingSplitButton } from './platform-ui/AIFloatingSplitButton.tsx';
import { AIFloatingContextMenuPopupContainer } from './AIFloatingContextMenuPopupContainer.tsx';
import { i18n as i18nDynamicAction } from './AIFloatingContextMenuGroup.tsx';
import { AIFloatingContextMenuPopupContent } from './AIFloatingContextMenuPopupContent.tsx';
import type { FeatureStates } from './popupStateTypes.ts';
import { PopupStateTypes, isFeaturePopupState } from './popupStateTypes.ts';

const absoluteWrapperStyles = xcss({
	display: 'flex',
	position: 'absolute',
	right: 'space.150',
	bottom: 'space.0',
});

const FloatingContextMenuStyles = css({
	position: 'fixed',
	bottom: token('space.600'),
	zIndex: '10',
	transform: 'translateX(-100%)',
});

type AIFloatingContextMenuProps = {
	contentId: string;
	contentType: string;
	spaceKey: string;
	lastModifiedDate?: string | null;
	pageCommentsSectionInView?: boolean;
	isLivePage?: boolean;
	isInObjectSideBar?: boolean;
	contentIdFromQuery?: string | null;
	contentBodyDynamic?: { representation?: string | null; value?: string | null } | null;
};

const dynamicActionTextMap: Partial<Record<PopupStateTypes, MessageDescriptor>> = {
	[PopupStateTypes.QuickSummary]: i18nDynamicAction.summarizeSectionLabel,
	[PopupStateTypes.Comments]: i18nDynamicAction.inlineCommentsMenuItemLabel,
	[PopupStateTypes.PageCatchup]: i18nDynamicAction.pageCatchupMenuItemLabel,
	[PopupStateTypes.BulkIssueCreate]: i18nDynamicAction.bulkIssueCreateMenuItemLabel,
};

const AIFloatingContextMenuWrapped: FC<AIFloatingContextMenuProps> = ({
	contentId,
	contentType,
	spaceKey,
	lastModifiedDate = '',
	pageCommentsSectionInView = false,
	isLivePage = false,
	isInObjectSideBar = false,
	contentIdFromQuery,
	contentBodyDynamic,
}) => {
	const intl = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const { edition, isAdminHubAIEnabled } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [popupState, setPopupState] = useState<PopupStateTypes>(PopupStateTypes.Menu);

	const { dynamicActionTarget, queryError } = useDynamicAIContext(
		contentId,
		contentType,
		pageCommentsSectionInView && !isLivePage,
		isLivePage,
		contentIdFromQuery,
		contentBodyDynamic,
	);
	const { pulseCount } = useAIFloatingButtonNudge(contentId, contentType);
	const { isSiteAdmin } = useContext(SPAViewContext);

	const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });

	const isDynamicActionSelected = isOpen && popupState !== PopupStateTypes.Menu;

	// environment-aware enablement via feature encoding
	const isAIFeatureEnabled = isFeatureEnabled('confluence', 'confluence-ai');
	const isEnabled =
		isAIFeatureEnabled &&
		(isAIEnabledForContent ||
			(isSiteAdmin && !isAdminHubAIEnabled) ||
			(edition !== ConfluenceEdition.PREMIUM && !isAdminHubAIEnabled));

	const getDynamicActionText = () => {
		if (dynamicActionTarget && !isOpen) {
			return intl.formatMessage(dynamicActionTextMap[dynamicActionTarget] as MessageDescriptor);
		} else if (isOpen && isFeaturePopupState(popupState)) {
			return intl.formatMessage(dynamicActionTextMap[popupState] as MessageDescriptor);
		} else {
			return undefined;
		}
	};

	const [prevDynamicActionTarget, setPrevDynamicActionTarget] = useState<
		FeatureStates.Comments | FeatureStates.PageCatchup | FeatureStates.QuickSummary | null
	>(null);

	if (dynamicActionTarget !== prevDynamicActionTarget) {
		setPrevDynamicActionTarget(dynamicActionTarget);
		if (dynamicActionTarget) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'shown',
					actionSubject: 'button',
					actionSubjectId: 'aiDynamicActionButton',
					source: 'page',
					attributes: {
						contentId,
						dynamicActionTarget,
						isLivePage,
					},
				},
			}).fire();
		}
	}

	const onClose = useCallback(() => {
		setIsOpen(false);
		setPopupState(PopupStateTypes.Menu);
	}, []);

	const onDynamicActionClick = useCallback(() => {
		if (isOpen) {
			onClose();
		} else if (dynamicActionTarget) {
			setIsOpen(true);
			setPopupState(dynamicActionTarget);
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'page',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'aiDynamicActionButton',
				attributes: {
					contentId,
					dynamicActionTarget,
					toggleClose: isOpen,
					isLivePage,
				},
			},
		}).fire();
	}, [contentId, createAnalyticsEvent, isOpen, onClose, dynamicActionTarget, isLivePage]);

	const onMenuButtonClick = useCallback(() => {
		if (isOpen && popupState !== PopupStateTypes.Menu && isAdminHubAIEnabled) {
			setPopupState(PopupStateTypes.Menu);
		} else if (isOpen) {
			onClose();
		} else if (!isAdminHubAIEnabled && edition !== ConfluenceEdition.PREMIUM) {
			setIsOpen(true);
			setPopupState(PopupStateTypes.Upsell);
		} else if (!isAdminHubAIEnabled && isSiteAdmin) {
			setIsOpen(true);
			setPopupState(PopupStateTypes.OptIn);
		} else {
			setIsOpen(true);
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'page',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'aiActionMenuButton',
				attributes: {
					contentId,
					edition,
					toggleClose: isOpen,
					isLivePage,
				},
			},
		}).fire();
	}, [
		contentId,
		createAnalyticsEvent,
		edition,
		isOpen,
		onClose,
		popupState,
		isAdminHubAIEnabled,
		isSiteAdmin,
		isLivePage,
	]);

	const popupComponent = () => {
		if (popupState === PopupStateTypes.Menu) {
			return AIFloatingContextMenuPopupContainer;
		}

		return CustomPopupContainer;
	};

	if (!isEnabled) {
		return null;
	}

	return (
		<>
			{queryError && <ErrorDisplay error={queryError} />}
			<Popup
				isOpen={isOpen}
				onClose={onClose}
				offset={[0, 6]}
				content={() => (
					<AIFloatingContextMenuPopupContent
						popupState={popupState}
						contentId={contentId}
						contentType={contentType}
						spaceKey={spaceKey}
						lastModifiedDate={lastModifiedDate ?? ''}
						source="view-page"
						onClose={onClose}
						setPopupState={setPopupState}
						setIsOpen={setIsOpen}
						isLivePage={isLivePage}
					/>
				)}
				shouldRenderToParent
				placement="top-end"
				popupComponent={popupComponent()}
				zIndex={
					popupState !== PopupStateTypes.Menu && fg('platform-dst-motion-uplift-popup')
						? 1
						: undefined
				}
				trigger={(triggerProps) => (
					<ContextualButtonPulse pulseCount={pulseCount} isInObjectSideBar={isInObjectSideBar}>
						{!isAdminHubAIEnabled ? (
							<AIButtonUI {...triggerProps} onClick={onMenuButtonClick} />
						) : (
							<AIFloatingSplitButton
								isDynamicActionSelected={isDynamicActionSelected}
								triggerProps={triggerProps}
								onMenuButtonClick={onMenuButtonClick}
								onDynamicActionClick={onDynamicActionClick}
								dynamicActionText={getDynamicActionText()}
								isMenuOpen={isOpen && popupState === PopupStateTypes.Menu}
								isInObjectSideBar={isInObjectSideBar}
							/>
						)}
					</ContextualButtonPulse>
				)}
			/>
		</>
	);
};

export const AIFloatingContextMenu = (props: AIFloatingContextMenuProps) => {
	return (
		<ErrorBoundary attribution={Attribution.SMARTS}>
			<CommentCountsProvider contentId={props.contentId}>
				{props.isInObjectSideBar ? (
					<AIFloatingContextMenuWrapped {...props} />
				) : (
					<Box xcss={absoluteWrapperStyles}>
						<div css={[FloatingContextMenuStyles]}>
							<AIFloatingContextMenuWrapped {...props} />
						</div>
					</Box>
				)}
			</CommentCountsProvider>
		</ErrorBoundary>
	);
};
